/**
 * Доступ на стороне клиента.
 *
 * Интерфейс обязан показывать ровно то, что разрешит сервер, — иначе человек
 * увидит замок там, где его нет, или наоборот упрётся в отказ после того, как
 * ввёл данные. Поэтому уровень и остаток берутся с сервера, а правила — из
 * общего каталога ядра.
 *
 * Отдельное правило про язык отказа: предлагать оплату можно **только**
 * когда возможность готова, данные есть, сеть есть, а тариф не позволяет.
 * «Позже», «не хватает данных» и «нет сети» — это не предложение купить.
 */
import { useEffect, useState } from 'react';
import { CATALOG, checkAccess, type FeatureKey, type Tier } from '@skoka/core';
import { api, unwrap } from './sync.js';

/** Счётчик объёма: сколько потрачено, осталось и всего на этот месяц подписки. */
export interface Meter { used: number; left: number; limit: number; window: string }

export interface AccessState {
  tier: Tier;
  analysis: Meter;
  receipt: Meter;
  /** Запись голосом и словами: свой пакет, не общий с чеками. */
  phrase: Meter;
  /** Когда обновятся объёмы Pro — в число оплаты. У пробного и Free — `null`. */
  resetsOn: string | null;
  /** Конец Pro или пробного (ISO). */
  endsAt: string | null;
  /** Автопродление включено. */
  renews: boolean;
  /** Продление не прошло: Pro работает до этого дня, пока пробуем снова. */
  graceUntil: string | null;
  /** Пробный: можно ли начать, показан ли лист «Неделя — бесплатно», что успел за пробный. */
  trial: {
    days: number; available: boolean; offerSeen: boolean;
    usage?: { phrase: number; receipt: number; analysis: number }; endedAt?: string | null;
  };
  /** Принимается ли оплата. Пока нет — экран Pro предлагает пробный период. */
  billing: boolean;
  /** Цена первого года по скидке раннего доступа; `null` — скидки нет. */
  earlyYear: number | null;
  /**
   * Первый разбор уже израсходован.
   *
   * Отдельно от месячного счётчика: стартовое право однократно и считается за
   * всё время. Без этого признака человек, потративший разбор гостем, после
   * входа снова видел обещание «первый разбор бесплатно».
   */
  firstUsed: boolean;
  /** Сервер недоступен: показываем локальные расчёты, не запирая человека. */
  offline: boolean;
  /**
   * Можно ли поставить уровень руками.
   *
   * Пока приёма платежей нет, это единственный способ увидеть Pro. Сервер
   * сам говорит, открыт ли такой путь: клиенту не положено об этом гадать.
   */
  switchable: boolean;
}

const NO_METER: Meter = { used: 0, left: 0, limit: 0, window: '' };

export const UNKNOWN_ACCESS: AccessState = {
  tier: 'free',
  analysis: NO_METER,
  receipt: NO_METER,
  phrase: NO_METER,
  resetsOn: null,
  endsAt: null,
  renews: false,
  graceUntil: null,
  trial: { days: 7, available: false, offerSeen: true },
  billing: false,
  earlyYear: null,
  firstUsed: false,
  offline: true,
  switchable: false,
};

/** Что открыто и сколько разборов осталось. Без сессии — про гостя. */
export async function fetchAccess(): Promise<AccessState> {
  try {
    type Raw = { used?: number; left?: number; limit?: number; window?: string };
    const body = await unwrap(await api('/access')) as {
      tier?: Tier; analysis?: Raw; receipt?: Raw; phrase?: Raw;
      firstAnalysisUsed?: boolean; switchable?: boolean;
      resetsOn?: string | null; endsAt?: string | null; renews?: boolean; graceUntil?: string | null;
      trial?: {
        days?: number; available?: boolean; offerSeen?: boolean;
        usage?: { phrase: number; receipt: number; analysis: number }; endedAt?: string | null;
      };
      billing?: boolean; earlyYear?: number | null;
    };
    const meter = (m: Raw | undefined): Meter => ({
      used: m?.used ?? 0, left: m?.left ?? 0,
      limit: m?.limit ?? (m?.used ?? 0) + (m?.left ?? 0), window: m?.window ?? '',
    });
    return {
      tier: body.tier ?? 'free',
      analysis: meter(body.analysis),
      receipt: meter(body.receipt),
      phrase: meter(body.phrase),
      resetsOn: body.resetsOn ?? null,
      endsAt: body.endsAt ?? null,
      renews: body.renews === true,
      graceUntil: body.graceUntil ?? null,
      trial: {
        days: body.trial?.days ?? 7,
        available: body.trial?.available === true,
        offerSeen: body.trial?.offerSeen !== false,
        ...(body.trial?.usage ? { usage: body.trial.usage, endedAt: body.trial.endedAt ?? null } : {}),
      },
      billing: body.billing === true,
      earlyYear: typeof body.earlyYear === 'number' ? body.earlyYear : null,
      firstUsed: body.firstAnalysisUsed === true,
      offline: false,
      switchable: body.switchable === true,
    };
  } catch {
    // Нет сети — это не «нет доступа»: расчёты и записи работают как прежде.
    return UNKNOWN_ACCESS;
  }
}

export interface FeatureView {
  allowed: boolean;
  /** Короткая метка рядом с действием: «Pro», «Позже» либо пусто. */
  badge: string;
  /** Что сказать человеку, если действие недоступно. */
  message: string;
  /** Показывать ли переход к тарифу. Только при тарифной причине. */
  offerUpgrade: boolean;
  /**
   * Показывать ли регистрацию.
   *
   * Отдельно от оплаты: гостю, у которого кончился стартовый разбор, нужен
   * аккаунт. Предложить ему купить значило бы взять плату за бесплатное.
   */
  offerSignup: boolean;
  /** Что за возможность и почему закрыта — для отчёта владельцу об упорах в Pro. */
  feature?: FeatureKey;
  reason?: string;
}

/**
 * Как показать возможность.
 *
 * Метка берётся из причины отказа, а не из тарифа: неготовая функция
 * помечается «Позже» даже у Pro, потому что купить её нельзя.
 */
export function featureView(
  feature: FeatureKey,
  access: AccessState,
  opts: { dataReady?: boolean } = {},
): FeatureView {
  // Счётчик берётся тот, которым возможность и меряется.
  //
  // Раньше сюда всегда шёл расход разборов: у Pro со ста использованными
  // разборами и нетронутым пакетом чеков форма документа оказывалась закрыта,
  // а в обратном случае интерфейс звал загружать чек, который сервер тут же
  // отвергнет. Два разных пакета — два разных счётчика.
  const meter = CATALOG.find((f) => f.key === feature)?.meter;
  const used = meter === 'receipt'
    ? access.receipt.used
    : meter === 'phrase'
      ? access.phrase.used
      : access.analysis.used;

  const verdict = checkAccess({
    feature,
    tier: access.tier,
    ...(meter ? { used } : {}),
    online: !access.offline,
    ...(opts.dataReady === undefined ? {} : { dataReady: opts.dataReady }),
  });

  if (verdict.allowed) {
    return { allowed: true, badge: '', message: '', offerUpgrade: false, offerSignup: false };
  }

  return {
    allowed: false,
    badge: verdict.reason === 'tier' ? 'Pro' : verdict.reason === 'not_released' ? 'Позже' : '',
    message: verdict.message,
    offerUpgrade: verdict.reason === 'tier',
    // Гостю нужен аккаунт, а не деньги: дальше это бесплатно.
    offerSignup: verdict.reason === 'signup',
    feature,
    ...(verdict.reason ? { reason: verdict.reason } : {}),
  };
}

/**
 * Подпись об остатке: «осталось 2 из 10».
 *
 * Показывается рядом с советником, а не всплывающим окном поверх операции:
 * счётчик — это сведения, а не требование действия.
 */
export function quotaLabel(access: AccessState): string | null {
  if (access.offline) return null;
  // Счётчик — принадлежность оплаченного пакета.
  //
  // У Free пакета нет вовсе: есть один бесплатный разбор и дальше доступ по
  // подписке. Показывать ему «0 из 1» значило бы выдавать отсутствие подписки
  // за исчерпанный лимит — и требовать денег там, где человеку просто не
  // предлагали Pro.
  if (access.tier !== 'pro' && access.tier !== 'trial') return null;

  // Остаток и объём — с сервера: он знает месяц подписки и действующий объём.
  const left = access.analysis.left;
  const total = access.analysis.limit || access.analysis.used + left;
  if (total === 0) return null;
  if (left === 0) {
    return access.resetsOn ? `Разборы закончились · обновятся ${dayLabel(access.resetsOn)}` : 'Разборы пробного периода закончились';
  }
  // Пока разборов много, счётчик не нужен: он превращается в напоминание
  // о лимите там, где лимит ещё далеко.
  if (left > 3) return null;
  return `Осталось ${left} из ${total} разборов`;
}

/**
 * Что показать в блоке советника человеку без подписки.
 *
 * Состояния из FINANCIAL_ADVISOR §5.1. Разница между ними существенна:
 * «первый разбор ещё не получен» — это приглашение к бесплатному действию,
 * «первый разбор получен» — рассказ о том, что даёт Pro, а «подписка
 * закончилась» — о том, что сохранилось. Один общий замок на всех трёх
 * состояниях превращает приложение в витрину.
 */
export type AdvisorGate =
  | { kind: 'open' }
  /** Первый разбор ещё доступен и бесплатен. */
  | { kind: 'first'; title: string; note: string; action: string }
  /** Первый разбор получен: диалог — в пробном периоде и Pro. */
  | { kind: 'offer'; title: string; note: string; action: string }
  /** Нужен аккаунт: пробный период и Pro живут на нём. */
  | { kind: 'signup'; title: string; note: string; action: string };

export function advisorGate(access: AccessState): AdvisorGate {
  if (access.offline) return { kind: 'open' };

  // Порядок важен: у платящего вопрос «первый разбор или нет» не возникает.
  const more = checkAccess({
    feature: 'extra_analysis', tier: access.tier,
    used: access.analysis.used, online: true, dataReady: true,
  });
  if (more.allowed) return { kind: 'open' };

  // Стартовое право считается своим счётчиком: месячный о нём не знает, и
  // израсходованный гостем разбор иначе обещался бы человеку второй раз.
  const first = checkAccess({
    feature: 'first_analysis', tier: access.tier,
    used: access.firstUsed ? 1 : 0, online: true, dataReady: true,
  });
  if (first.allowed) {
    return {
      kind: 'first',
      title: 'Первый разбор твоих денег — бесплатно',
      note: 'Посмотрю на картину целиком и скажу, что в ней главное и с чего начинать.',
      action: 'Получить первый разбор',
    };
  }

  if (more.reason === 'signup') {
    return {
      kind: 'signup',
      title: 'Финансовый советник',
      note: more.message,
      action: 'Создать аккаунт',
    };
  }
  // Кончившийся пакет у платящего — не повод продавать то, что уже куплено.
  if (more.reason === 'quota') {
    return {
      kind: 'open',
    };
  }
  /*
    Первый разбор человек уже получил, новые — в Pro.

    Говорим об этом словами, которыми человек думает: не «доступ к функции
    ограничен тарифом», а «первый разбор был, следующий — в Pro». И сразу
    называем, что остаётся бесплатным: иначе закрытая дверь читается как
    «приложение больше ничего не умеет», хотя предупреждения, календарь и
    расчёты работают ровно так же.
  */
  return {
    kind: 'offer',
    title: 'Разбор уже был. Следующий — в Pro',
    note: 'SKOKA посмотрит на твою картину заново, объяснит, что изменилось, и '
      + 'предложит, что сделать. Предупреждения о нехватке денег, календарь платежей '
      + 'и все расчёты остаются бесплатными.',
    action: 'Подключить Pro',
  };
}

/**
 * Поставить уровень подписки руками.
 *
 * Работает, только пока сервер разрешает (`switchable`). Проверять Pro как-то
 * надо, а приёма платежей ещё нет.
 */
export async function setTier(tier: Tier): Promise<AccessState> {
  await unwrap(await api('/access/tier', {
    method: 'POST',
    body: JSON.stringify({ tier }),
  }));
  accessChanged();
  return fetchAccess();
}

/**
 * Доступ изменился: включён пробный, оплачен Pro, сменён уровень. Каждый блок,
 * который держит доступ у себя, перечитывает его — иначе человек заплатил и
 * до перезагрузки видит замки (кнопка «Спроси SKOKA» узнавала о Pro только
 * при смене почты).
 */
export function accessChanged(): void {
  window.dispatchEvent(new Event('skoka:access'));
}

/** Перечитать доступ, когда он изменился (пробный, оплата, смена уровня). Возвращает отписку. */
export function onAccessChanged(cb: () => void): () => void {
  window.addEventListener('skoka:access', cb);
  return () => window.removeEventListener('skoka:access', cb);
}

/**
 * Доступ, который сам обновляется: после `accessChanged()` и при возврате в
 * приложение (пробный мог закончиться, пока оно было свёрнуто).
 */
export function useAccess(deps: readonly unknown[] = []): AccessState | null {
  const [access, setAccess] = useState<AccessState | null>(null);
  useEffect(() => {
    let alive = true;
    const load = () => { void fetchAccess().then((a) => { if (alive) setAccess(a); }); };
    load();
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener('skoka:access', load);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      window.removeEventListener('skoka:access', load);
      document.removeEventListener('visibilitychange', onVisible);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return access;
}

/** Начать пробный период: 7 дней, один раз на аккаунт. */
export async function startTrial(): Promise<AccessState> {
  await unwrap(await api('/access/trial', { method: 'POST' }));
  accessChanged();
  return fetchAccess();
}

/** Лист «Неделя — бесплатно» показан: второй раз — ни здесь, ни на другом устройстве. */
export async function trialOfferSeen(): Promise<void> {
  await unwrap(await api('/access/trial-offer-seen', { method: 'POST' }));
}

const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

/**
 * «2026-11-21» → «21 ноября»; не в этом году — «21 октября 2027»: иначе срок
 * годовой подписки читался как сегодняшнее число.
 */
export function dayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!m || !d) return 'в следующем месяце';
  return y && y !== new Date().getFullYear() ? `${d} ${MONTHS[m - 1]} ${y}` : `${d} ${MONTHS[m - 1]}`;
}
